import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Download, Filter, X, ChevronLeft, ChevronRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { adminHttp, API, STATUS_LABELS, STATUS_COLORS, STATUS_ORDER, scoreColor, TOKEN_KEY } from "../../lib/adminApi";
import TierBadge from "../components/TierBadge";

const SOURCE_OPTIONS = [
  { v: "", l: "Todos" },
  { v: "quote_form", l: "Formulario" },
  { v: "catalog_download", l: "Catálogo" },
  { v: "popup", l: "Pop-up" },
];

const PROJECT_TYPES = {
  malla_sombra: "Malla sombra",
  membrana_pvc: "PVC",
  tensoestructura_arquitectonica: "Tensoestructura",
  techo_canchas: "Canchas",
  cubierta_industrial: "Industrial",
  plaza_centro_comercial: "Plaza",
  residencial_premium: "Residencial",
  institucion_educativa: "Educativa",
  otro: "Otro",
};

export default function LeadsList() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = useMemo(() => ({
    status: params.get("status") || "",
    source: params.get("source") || "",
    min_score: params.get("min_score") || "",
    max_score: params.get("max_score") || "",
    city: params.get("city") || "",
    project_type: params.get("project_type") || "",
    tier: params.get("tier") || "",
    utm_source: params.get("utm_source") || "",
    search: params.get("search") || "",
    page: Number(params.get("page") || 1),
    sort: params.get("sort") || "created_at_desc",
    limit: 25,
  }), [params]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) query.set(k, v); });
    adminHttp.get(`/admin/leads?${query.toString()}`)
      .then(({ data }) => { if (alive) setData(data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filters]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === null) next.delete(key); else next.set(key, String(value));
    if (key !== "page") next.set("page", "1");
    setParams(next);
  };

  const clearFilters = () => setParams(new URLSearchParams());

  const activeCount = Object.entries(filters).filter(([k, v]) => v && !["page", "sort", "limit"].includes(k)).length;

  const exportCsv = async () => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (!["page", "sort", "limit"].includes(k) && v) query.set(k, v);
    });
    // We need auth header — fetch blob manually
    const token = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(`${API}/admin/leads/export.csv?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `velarias_leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="admin-leads-list">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="divider-accent" /><span className="kicker">CRM</span>
          </div>
          <h1 className="font-display font-black tracking-tightest text-3xl md:text-5xl">
            Leads <span className="text-accent">({data.total})</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-3 border border-[#DDE2D5] font-mono uppercase text-[11px] tracking-widest hover:border-accent hover:text-accent"
            data-testid="leads-toggle-filters"
          >
            <Filter className="w-4 h-4" /> Filtros {activeCount > 0 && <span className="bg-accent text-[#1F2937] px-1.5">{activeCount}</span>}
          </button>
          <button onClick={exportCsv} className="btn-accent py-3 px-5 text-xs" data-testid="leads-export-csv">
            <Download className="w-4 h-4" /> Exportar CSV
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
        <input
          type="search"
          placeholder="Buscar por nombre, email o teléfono…"
          value={filters.search}
          onChange={(e) => setParam("search", e.target.value)}
          className="w-full bg-white border border-[#DDE2D5] pl-10 pr-4 py-3 text-[#1F2937] placeholder:text-[#6B7280] focus:outline-none focus:border-accent font-mono text-sm"
          data-testid="leads-search-input"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6">
        {/* Table */}
        <div className="bg-white border border-[#DDE2D5] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leads-table">
              <thead>
                <tr className="bg-[#F2F4ED] text-left font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Teléfono</th>
                  <th className="px-4 py-3">Ciudad</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">m²</th>
                  <th className="px-4 py-3 text-center">Score</th>
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Asignado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={11} className="px-4 py-10 text-center text-[#6B7280] font-mono text-xs">Cargando…</td></tr>
                )}
                {!loading && data.items.length === 0 && (
                  <tr><td colSpan={11} className="px-4 py-10 text-center text-[#6B7280] font-mono text-xs">Sin resultados con los filtros actuales.</td></tr>
                )}
                {!loading && data.items.map((l) => (
                  <tr key={l.id} className="border-t border-[#EAECEA] hover:bg-[#F2F4ED]" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-[#4B5563] whitespace-nowrap">
                      {format(parseISO(l.created_at), "dd/MM HH:mm")}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#1F2937] font-semibold">{l.full_name || l.email || "—"}</td>
                    <td className="px-4 py-3 text-sm text-[#374151] font-mono whitespace-nowrap">{l.phone || "—"}</td>
                    <td className="px-4 py-3 text-sm text-[#374151]">{l.city || "—"}</td>
                    <td className="px-4 py-3 text-sm text-[#374151]">{PROJECT_TYPES[l.project_type] || "—"}</td>
                    <td className="px-4 py-3 text-sm text-[#374151] font-mono">{l.square_meters || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-display font-black text-lg tabular-nums" style={{ color: scoreColor(l.score || 0) }}>
                        {l.score || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3"><TierBadge tier={l.tier} /></td>
                    <td className="px-4 py-3">
                      <span
                        className="font-mono text-[10px] uppercase px-2 py-1 tracking-wider whitespace-nowrap"
                        style={{ background: STATUS_COLORS[l.status || "new"], color: "#1F2937" }}
                      >
                        {STATUS_LABELS[l.status || "new"]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#4B5563]">{l.assigned_to_name || "—"}</td>
                    <td className="px-4 py-3">
                      <Link to={`/admin/leads/${l.id}`} className="font-mono uppercase text-[10px] tracking-widest text-accent hover:underline">Ver</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="border-t border-[#DDE2D5] px-4 py-3 flex items-center justify-between text-xs font-mono text-[#4B5563]">
            <span>Página {data.page} de {Math.max(1, data.pages)}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setParam("page", Math.max(1, filters.page - 1))}
                disabled={filters.page <= 1}
                className="p-2 border border-[#DDE2D5] hover:border-accent disabled:opacity-30"
                data-testid="leads-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setParam("page", Math.min(Math.max(1, data.pages), filters.page + 1))}
                disabled={filters.page >= data.pages}
                className="p-2 border border-[#DDE2D5] hover:border-accent disabled:opacity-30"
                data-testid="leads-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters sidebar */}
        {filtersOpen && (
          <aside className="w-full lg:w-72 bg-white border border-[#DDE2D5] p-5 space-y-5 self-start" data-testid="leads-filters-panel">
            <div className="flex items-center justify-between">
              <span className="kicker">Filtros</span>
              <button onClick={clearFilters} className="text-[#6B7280] hover:text-accent" aria-label="Limpiar">
                <X className="w-4 h-4" />
              </button>
            </div>

            <FilterSelect label="Status" value={filters.status} onChange={(v) => setParam("status", v)} options={[{ v: "", l: "Todos" }, ...STATUS_ORDER.map((s) => ({ v: s, l: STATUS_LABELS[s] }))]} />
            <FilterSelect label="Fuente" value={filters.source} onChange={(v) => setParam("source", v)} options={SOURCE_OPTIONS} />
            <FilterSelect label="Tipo de proyecto" value={filters.project_type} onChange={(v) => setParam("project_type", v)} options={[{ v: "", l: "Todos" }, ...Object.entries(PROJECT_TYPES).map(([v, l]) => ({ v, l }))]} />
            <FilterSelect label="Tier" value={filters.tier} onChange={(v) => setParam("tier", v)} options={[
              { v: "", l: "Todos" },
              { v: "premium", l: "Premium" },
              { v: "medium", l: "Medium" },
              { v: "small", l: "Small" },
              { v: "unclassified", l: "Sin clasificar" },
            ]} />

            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Ciudad</span>
              <input type="text" value={filters.city} onChange={(e) => setParam("city", e.target.value)} className="mt-2 w-full bg-white border border-[#DDE2D5] px-3 py-2 text-[#1F2937] text-sm focus:outline-none focus:border-accent" placeholder="Monterrey…" />
            </div>

            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Score</span>
              <div className="mt-2 flex items-center gap-2">
                <input type="number" min={0} max={100} value={filters.min_score} onChange={(e) => setParam("min_score", e.target.value)} placeholder="0" className="w-full bg-white border border-[#DDE2D5] px-2 py-2 text-[#1F2937] text-xs font-mono" />
                <span className="text-[#9CA3AF]">—</span>
                <input type="number" min={0} max={100} value={filters.max_score} onChange={(e) => setParam("max_score", e.target.value)} placeholder="100" className="w-full bg-white border border-[#DDE2D5] px-2 py-2 text-[#1F2937] text-xs font-mono" />
              </div>
            </div>

            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">UTM source</span>
              <input type="text" value={filters.utm_source} onChange={(e) => setParam("utm_source", e.target.value)} className="mt-2 w-full bg-white border border-[#DDE2D5] px-3 py-2 text-[#1F2937] text-sm focus:outline-none focus:border-accent" placeholder="google, facebook…" />
            </div>

            <FilterSelect label="Orden" value={filters.sort} onChange={(v) => setParam("sort", v)} options={[
              { v: "created_at_desc", l: "Más recientes" },
              { v: "created_at_asc", l: "Más antiguos" },
              { v: "score_desc", l: "Mayor score" },
              { v: "score_asc", l: "Menor score" },
            ]} />
          </aside>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div>
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-white border border-[#DDE2D5] px-3 py-2 text-[#1F2937] text-sm focus:outline-none focus:border-accent"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
